import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import PricingToggle from "./pricingToggle";
import { writeCart } from "../../modules/checkoutHelpers";
import {
  DashboardImage,
  CustomerInterface,
  PablooDashboardImage,
  StoreCreditCheckoutMobile,
} from "../../assets/assets";

// ─── Data ────────────────────────────────────────────────────────────────────

const growthTiers = [
  {
    label: "S",
    monthlyPrice: "$149",
    annualPrice: "$119",
    customerAccounts: "10,000",
    storeAccounts: "3",
    creditEvents: "16,000",
    smsIncluded: "2,000",
  },
  {
    label: "M",
    monthlyPrice: "$299",
    annualPrice: "$239",
    customerAccounts: "25,000",
    storeAccounts: "5",
    creditEvents: "40,000",
    smsIncluded: "8,000",
  },
  {
    label: "L",
    monthlyPrice: "$549",
    annualPrice: "$439",
    customerAccounts: "60,000",
    storeAccounts: "10",
    creditEvents: "96,000",
    smsIncluded: "25,000",
  },
];

const starterData = {
  customerAccounts: "1,000",
  storeAccounts: "1",
  creditEvents: "5,000",
  apiAccess: false,
  support: "Email",
};

const growthSharedData = {
  apiAccess: true,
  support: "Priority",
};

// ─── Small utilities ─────────────────────────────────────────────────────────

function CheckIcon({ size = 18 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="inline-block"
    >
      <circle cx={12} cy={12} r={10} fill="#15803d" fillOpacity={0.13} />
      <path
        d="M7.5 12.5l3 3 6-6"
        stroke="#15803d"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function XIcon({ size = 18 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="inline-block"
    >
      <path
        d="M17 7L7 17M7 7l10 10"
        stroke="#cbd5e1"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </svg>
  );
}

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-block ml-1 align-middle">
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="cursor-help text-gray-400 hover:text-gray-500"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width={13} height={13} viewBox="0 0 24 24" fill="none">
          <circle cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={1.5} />
          <path d="M12 11v5M12 8v.01" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
        </svg>
      </span>
      {visible && (
        <span className="absolute z-30 left-1/2 -translate-x-1/2 bottom-6 w-60 bg-gray-900 text-white text-xs rounded-xl px-3 py-2.5 shadow-xl pointer-events-none leading-relaxed">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
}

// ─── Growth tier toggle ───────────────────────────────────────────────────────

function GrowthTierToggle({ selected, onChange }) {
  return (
    <div className="flex items-center justify-center mt-3 mb-1">
      <div className="flex rounded-full bg-green-800 border border-green-700 p-0.5 gap-0.5">
        {growthTiers.map((t) => (
          <button
            key={t.label}
            onClick={() => onChange(t.label)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all duration-150 ${
              selected === t.label
                ? "bg-white text-green-900 shadow-sm"
                : "text-green-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Plan card ────────────────────────────────────────────────────────────────

function StarterCard({ frequency, isSignedIn, onSelect }) {
  const displayPrice = frequency === "annually" ? "$39" : "$49";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-7 flex flex-col">
      <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
        Starter
      </p>
      <div className="flex items-end gap-1.5 mb-0.5">
        <span className="text-4xl font-extrabold text-gray-900 tracking-tight leading-none">
          {displayPrice}
        </span>
        <span className="text-sm text-gray-400 mb-1">/ mo</span>
      </div>
      <p className="text-xs text-gray-400 mb-6">
        {frequency === "annually" ? "billed annually" : "billed monthly"}
      </p>

      <ul className="space-y-2.5 text-sm text-gray-600 mb-8 flex-1">
        <li className="flex items-center gap-2.5">
          <CheckIcon /> 1,000 customer accounts
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> 1 Shopify store
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> 5,000 credit events / mo
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Unlimited credit issued
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Unlimited team accounts
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Unlimited email notifications
        </li>
        <li className="flex items-center gap-2.5 text-gray-300">
          <XIcon /> SMS notifications
        </li>
        <li className="flex items-center gap-2.5 text-gray-300">
          <XIcon /> API access
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Chat support (limited hours)
        </li>
      </ul>

      {isSignedIn ? (
        <button
          onClick={() => onSelect(20)}
          className="w-full text-center rounded-xl border border-gray-300 text-gray-800 text-sm font-semibold py-3 px-4 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          Get started
        </button>
      ) : (
        <a
          href="https://app.pabloo.com/sign-up"
          className="w-full text-center rounded-xl border border-gray-300 text-gray-800 text-sm font-semibold py-3 px-4 hover:bg-gray-50 transition-colors"
        >
          Get started
        </a>
      )}
    </div>
  );
}

const GROWTH_TIER_PRODUCT_IDS = { S: 21, M: 22, L: 23 };

function GrowthCard({ frequency, isSignedIn, onSelect }) {
  const [selectedTier, setSelectedTier] = useState("M");
  const tier = growthTiers.find((t) => t.label === selectedTier);
  const displayPrice =
    frequency === "annually" ? tier.annualPrice : tier.monthlyPrice;

  return (
    <div className="rounded-2xl border-2 border-green-800 bg-green-900 p-7 flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-bold uppercase tracking-widest text-green-400">
          Growth
        </p>
        <span className="inline-block bg-green-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
          Most popular
        </span>
      </div>

      <GrowthTierToggle selected={selectedTier} onChange={setSelectedTier} />

      <div className="flex items-end gap-1.5 mt-4 mb-0.5">
        <span className="text-4xl font-extrabold text-white tracking-tight leading-none">
          {displayPrice}
        </span>
        <span className="text-sm text-green-600 mb-1">/ mo</span>
      </div>
      <p className="text-xs text-green-700 mb-6">
        {frequency === "annually" ? "billed annually" : "billed monthly"}
      </p>

      <ul className="space-y-2.5 text-sm text-green-100 mb-8 flex-1">
        <li className="flex items-center gap-2.5">
          <CheckIcon /> {tier.customerAccounts} customer accounts
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> {tier.storeAccounts} Shopify store{tier.storeAccounts !== "1" ? "s" : ""}
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> {tier.creditEvents} credit events / mo
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Unlimited credit issued
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Unlimited team accounts
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Unlimited email notifications
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> {tier.smsIncluded} SMS / mo included
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Full API access
        </li>
        <li className="flex items-center gap-2.5">
          <CheckIcon /> Chat 24/7 + priority support
        </li>
      </ul>

      {isSignedIn ? (
        <button
          onClick={() => onSelect(GROWTH_TIER_PRODUCT_IDS[selectedTier])}
          className="w-full text-center rounded-xl bg-green-500 text-white text-sm font-semibold py-3 px-4 hover:bg-green-400 transition-colors cursor-pointer"
        >
          Get started
        </button>
      ) : (
        <a
          href="https://app.pabloo.com/sign-up"
          className="w-full text-center rounded-xl bg-green-500 text-white text-sm font-semibold py-3 px-4 hover:bg-green-400 transition-colors"
        >
          Get started
        </a>
      )}
    </div>
  );
}

// ─── Comparison table ─────────────────────────────────────────────────────────

const columns = [
  { key: "starter", label: "Starter" },
  { key: "growthS", label: "Growth S" },
  { key: "growthM", label: "Growth M" },
  { key: "growthL", label: "Growth L" },
  { key: "managedEconomy", label: "Managed Economy" },
];

const tableRows = [
  {
    label: "Customer accounts",
    tooltip: "Wallets with a balance above zero. Empties don't count.",
    starter: "1,000",
    growthS: "10,000",
    growthM: "25,000",
    growthL: "60,000",
    managedEconomy: "200,000+",
  },
  {
    label: "Store accounts",
    tooltip: "One connected Shopify store per account.",
    starter: "1",
    growthS: "3",
    growthM: "5",
    growthL: "10",
    managedEconomy: "Unlimited",
  },
  {
    label: "Team accounts",
    tooltip: "Staff logins. Unlimited on every plan.",
    starter: "Unlimited",
    growthS: "Unlimited",
    growthM: "Unlimited",
    growthL: "Unlimited",
    managedEconomy: "Unlimited",
  },
  {
    label: "Credit events / mo",
    tooltip: "One issuance or one redemption. Dollar amount doesn't count.",
    starter: "5,000",
    growthS: "16,000",
    growthM: "40,000",
    growthL: "96,000",
    managedEconomy: "Unlimited",
  },
  {
    label: "Credit issued",
    tooltip: "Never metered. Same price at $10 issued and $10,000,000 issued.",
    starter: "Unlimited",
    growthS: "Unlimited",
    growthM: "Unlimited",
    growthL: "Unlimited",
    managedEconomy: "Unlimited",
  },
  {
    label: "Email notifications",
    tooltip: "Wallet and transaction emails sent on your behalf.",
    starter: "Unlimited",
    growthS: "Unlimited",
    growthM: "Unlimited",
    growthL: "Unlimited",
    managedEconomy: "Unlimited",
  },
  {
    label: "SMS notifications / mo",
    tooltip: "Included messages per month. Overages billed per message.",
    starter: "0",
    growthS: "2,000",
    growthM: "8,000",
    growthL: "25,000",
    managedEconomy: "150,000",
  },
  {
    label: "API access",
    tooltip: "Full REST API for custom integrations.",
    starter: false,
    growthS: true,
    growthM: true,
    growthL: true,
    managedEconomy: true,
    boolean: true,
  },
  {
    label: "Support",
    starter: "Chat (limited hours)",
    growthS: "Chat 24/7 + priority",
    growthM: "Chat 24/7 + priority",
    growthL: "Chat 24/7 + priority",
    managedEconomy: "Chat 24/7 + dedicated",
  },
];

function ComparisonTable({ frequency, isSignedIn, onSelect }) {
  function headerPrice(col) {
    const map = {
      starter: frequency === "annually" ? "$39" : "$49",
      growthS: frequency === "annually" ? "$119" : "$149",
      growthM: frequency === "annually" ? "$239" : "$299",
      growthL: frequency === "annually" ? "$439" : "$549",
      managedEconomy: "Custom",
    };
    return map[col.key];
  }

  const colProductIds = { starter: 20, growthS: 21, growthM: 22, growthL: 23, managedEconomy: null };
  const colCTA = {
    starter:       { label: "Get started", href: "https://app.pabloo.com/sign-up" },
    growthS:       { label: "Get started", href: "https://app.pabloo.com/sign-up" },
    growthM:       { label: "Get started", href: "https://app.pabloo.com/sign-up" },
    growthL:       { label: "Get started", href: "https://app.pabloo.com/sign-up" },
    managedEconomy:{ label: "Talk to sales", href: "/bookCall" },
  };

  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-5 px-6 font-medium text-gray-400 text-xs uppercase tracking-wider w-1/3">
              What's metered
            </th>
            {columns.map((col) => {
              const cta = colCTA[col.key];
              const isManaged = col.key === "managedEconomy";
              return (
                <th
                  key={col.key}
                  className={`py-5 px-4 text-center ${
                    col.key === "growthM" ? "bg-gray-50" : isManaged ? "bg-gray-900" : ""
                  }`}
                >
                  <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${isManaged ? "text-gray-400" : "text-gray-400"}`}>
                    {col.label}
                  </div>
                  <div className={`text-lg font-extrabold tracking-tight mb-3 ${isManaged ? "text-white" : "text-gray-900"}`}>
                    {headerPrice(col)}
                    {!isManaged && (
                      <span className="text-xs font-normal text-gray-400"> /mo</span>
                    )}
                  </div>
                  {isSignedIn && colProductIds[col.key] !== null ? (
                    <button
                      onClick={() => onSelect(colProductIds[col.key])}
                      className={`inline-block rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer border-0 ${
                        isManaged
                          ? "bg-green-500 text-white hover:bg-green-400"
                          : "border border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {cta.label}
                    </button>
                  ) : (
                    <a
                      href={cta.href}
                      className={`inline-block rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                        isManaged
                          ? "bg-green-500 text-white hover:bg-green-400"
                          : "border border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {cta.label}
                    </a>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row, i) => (
            <tr key={i} className="border-t border-gray-100 hover:bg-gray-50/60 transition-colors">
              <td className="py-4 px-6 text-gray-700 font-medium">
                {row.label}
                {row.tooltip && <InfoTooltip text={row.tooltip} />}
              </td>
              {columns.map((col) => {
                const val = row[col.key];
                return (
                  <td
                    key={col.key}
                    className={`py-4 px-4 text-center ${
                      col.key === "managedEconomy"
                        ? "bg-gray-900 text-gray-300"
                        : col.key === "growthM"
                        ? "bg-gray-50 text-gray-600"
                        : "text-gray-600"
                    }`}
                  >
                    {row.boolean ? (
                      val ? <CheckIcon size={20} /> : <XIcon size={20} />
                    ) : (
                      val
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── "How we count" definitions ───────────────────────────────────────────────

function DefinitionBlock({ eyebrow, heading, body, image, imageAlt, flip }) {
  return (
    <div
      className={`flex flex-col ${
        flip ? "md:flex-row-reverse" : "md:flex-row"
      } items-center gap-10 py-10`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold uppercase tracking-widest text-green-700 mb-2">
          {eyebrow}
        </p>
        <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-3 leading-snug">
          {heading}
        </h3>
        <p className="text-gray-500 text-base leading-relaxed">{body}</p>
      </div>
      {image && (
        <div className="flex-1 min-w-0">
          <img
            src={image}
            alt={imageAlt}
            className="w-full rounded-2xl object-cover shadow-md"
            style={{ maxHeight: 300 }}
          />
        </div>
      )}
    </div>
  );
}

// ─── SMS pricing table ────────────────────────────────────────────────────────

const smsRates = [
  { plan: "Starter",  included: "0",       na: null,  intl: null },
  { plan: "Growth S", included: "2,000",   na: 0.020, intl: 0.060 },
  { plan: "Growth M", included: "8,000",   na: 0.020, intl: 0.060 },
  { plan: "Growth L", included: "25,000",  na: 0.018, intl: 0.055 },
  { plan: "Managed",  included: "150,000", na: 0.012, intl: 0.045 },
];

function SmsPricingTable() {
  return (
    <div className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-16">
      <div className="px-6 pt-6 pb-4 border-b border-gray-100">
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight mb-1">SMS pricing</h2>
        <p className="text-gray-400 text-sm">Included messages reset each billing period. Overages are billed per message sent.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left py-3.5 px-6 font-medium text-gray-400 text-xs uppercase tracking-wider">Plan</th>
              <th className="text-center py-3.5 px-4 font-medium text-gray-400 text-xs uppercase tracking-wider">Included / mo</th>
              <th className="text-center py-3.5 px-4 font-medium text-gray-400 text-xs uppercase tracking-wider">US &amp; Canada</th>
              <th className="text-center py-3.5 px-4 font-medium text-gray-400 text-xs uppercase tracking-wider">Rest of world</th>
            </tr>
          </thead>
          <tbody>
            {smsRates.map((row, i) => (
              <tr key={row.plan} className={`border-b border-gray-50 ${i % 2 === 0 ? "" : "bg-gray-50/50"}`}>
                <td className="py-4 px-6 font-semibold text-gray-800">{row.plan}</td>
                <td className="py-4 px-4 text-center text-gray-700">{row.included}</td>
                <td className="py-4 px-4 text-center text-gray-700">
                  {row.na === null ? <span className="text-gray-300">Not available</span> : `$${row.na.toFixed(3)}`}
                </td>
                <td className="py-4 px-4 text-center text-gray-700">
                  {row.intl === null ? <span className="text-gray-300">Not available</span> : `$${row.intl.toFixed(3)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-6 py-5 space-y-2 border-t border-gray-100 bg-gray-50/50">
        <p className="text-gray-500 text-sm">Email is unlimited and free on every plan. Email reaches 99% of wallets. SMS reaches the 20% who have given you a phone number.</p>
        <p className="text-gray-500 text-sm">SMS is billed only on messages we send. A customer with no phone number on file is never billed.</p>
        <p className="text-gray-500 text-sm">Rest of world covers every market outside the United States and Canada. Carrier rates in Australia, Singapore, and the UAE are higher than North America.</p>
      </div>
    </div>
  );
}

// ─── CTA ─────────────────────────────────────────────────────────────────────

function CTASection() {
  return (
    <div className="relative rounded-3xl overflow-hidden bg-gray-900 px-8 py-14 text-center mb-16">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(34,197,94,0.18) 0%, transparent 65%)",
        }}
      />
      <p className="text-xs font-bold uppercase tracking-widest text-green-400 mb-3 relative">
        Managed Economy
      </p>
      <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-4 relative leading-tight">
        Your customers already have a wallet.<br className="hidden md:block" /> At your scale, who's running the strategy?
      </h2>
      <p className="text-gray-400 text-base max-w-xl mx-auto mb-8 relative leading-relaxed">
        Most platforms hand you a dashboard and walk away. Managed Economy is different — you get a dedicated Pabloo economist who designs, runs, and optimizes your entire store credit program. You set the direction. We run the numbers.
      </p>
      <div className="flex justify-center relative">
        <a
          href="/bookCall"
          className="rounded-xl bg-green-500 text-white text-sm font-bold py-3.5 px-10 hover:bg-green-400 transition-colors"
        >
          Talk to us
        </a>
      </div>
      <p className="text-xs text-gray-600 mt-4 relative">200,000+ customer accounts · Unlimited stores · Dedicated economist</p>
    </div>
  );
}

// ─── Billing transparency (exported for use on other pages) ──────────────────

export function BillingTransparencySection() {
  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
          Billing transparency
        </p>
        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
          How we count
        </h2>
      </div>

      <div className="divide-y divide-gray-100">

        <DefinitionBlock
          eyebrow="Customer accounts"
          heading="We count wallets with a balance — nothing else."
          body="A customer account is one shopper wallet that holds a balance greater than zero. The moment a wallet empties out — whether a customer spent it all or it expired — it stops counting on the next billing day. A store with 50,000 registered shoppers but only 800 active balances pays for 800. You never get charged for dormant customers."
          image={CustomerInterface}
          imageAlt="Customer wallet interface"
          flip={false}
        />

        <DefinitionBlock
          eyebrow="Store accounts"
          heading="Each connected Shopify store counts as one."
          body="A store account is a single Shopify storefront connected to Pabloo. If you run a flagship store and a wholesale portal as two separate Shopify stores, that counts as two store accounts. Growth plans support up to 10. If you're running 20 or more, talk to us about the Managed tier — stores are unlimited there."
          image={PablooDashboardImage}
          imageAlt="Pabloo dashboard showing multiple stores"
          flip={true}
        />

        <DefinitionBlock
          eyebrow="Credit events"
          heading="One event = one action. The dollar amount is irrelevant."
          body="A credit event is a single issuance or a single redemption — that's it. Whether you issue $0.50 as a welcome bonus or $5,000 to a high-value customer, each issuance is one event. Whether a customer redeems $2 or drains a $2,000 balance at checkout, each redemption is one event. We built it this way deliberately: volume pricing that scales with transactions, not with the size of your program, keeps your margins predictable no matter how aggressive your cashback rates are."
          image={DashboardImage}
          imageAlt="Credit event activity dashboard"
          flip={false}
        />

        <DefinitionBlock
          eyebrow="Credit issued"
          heading="Unlimited. We mean it."
          body="Credit issued is never metered on any plan. A merchant running a 10% cashback campaign on a $10,000,000 revenue month pays the exact same subscription price as a merchant issuing $50 of welcome credits. This is intentional — metering the dollar amount of credit issued would penalize the most successful programs. Your subscription covers the infrastructure; what you do with it is entirely up to you."
          image={StoreCreditCheckoutMobile}
          imageAlt="Store credit at checkout on mobile"
          flip={true}
        />

        <div className="py-10">
          <p className="text-xs font-bold uppercase tracking-widest text-green-700 mb-2">
            Team accounts
          </p>
          <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-3">
            Add every team member. Always free.
          </h3>
          <p className="text-gray-500 text-base leading-relaxed max-w-2xl">
            A team account is one merchant staff login — a customer service rep, a marketing manager, a developer, a co-founder. Team accounts are unlimited on every plan including Starter. We don't believe in charging more because your business has more than one person running it.
          </p>
        </div>

      </div>
    </div>
  );
}

// ─── Root export ──────────────────────────────────────────────────────────────

export default function PricingPlansGrid({ showBillingTransparency = true }) {
  const [frequency, setFrequency] = useState("annually");
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();

  const handlePlanSelect = (productIndex) => {
    writeCart({
      productLineItems: [{ productIndex, frequency: frequency === "annually" ? "year" : "month", quantity: 1 }],
      isMonthlyFrequency: frequency === "monthly",
    });
    navigate("/cart");
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4">

      {/* ── Page header ── */}
      <div className="text-center mb-12">
        <p className="text-xs font-bold uppercase tracking-widest text-green-700 mb-3">
          Pricing
        </p>
        <h1
          className="text-5xl md:text-6xl font-extrabold text-gray-900 tracking-tight leading-none mb-4"
          style={{ fontFamily: "P22 Mackinacpro, sans-serif" }}
        >
          Flat rate.<br className="hidden md:block" /> No surprises.
        </h1>
        <p className="text-gray-500 text-lg max-w-lg mx-auto leading-relaxed">
          One fixed price per month. As many orders, as much credit issued, as many team members as you need.
        </p>
      </div>

      {/* ── Toggle ── */}
      <div className="flex flex-col items-center mb-10 gap-2">
        <PricingToggle frequency={frequency} onFrequencyChange={setFrequency} />
        {frequency === "annually" && (
          <p className="text-green-700 text-xs font-semibold tracking-wide">
            Save up to 20% with annual billing
          </p>
        )}
      </div>

      {/* ── Plan cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl mx-auto mb-16">
        <StarterCard frequency={frequency} isSignedIn={isSignedIn} onSelect={handlePlanSelect} />
        <GrowthCard  frequency={frequency} isSignedIn={isSignedIn} onSelect={handlePlanSelect} />
      </div>

      {/* ── Comparison table ── */}
      <div className="mb-4">
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight mb-1">
          Full breakdown
        </h2>
        <p className="text-gray-400 text-sm">Every limit, every plan, side by side.</p>
      </div>
      <div className="mb-16">
        <ComparisonTable frequency={frequency} isSignedIn={isSignedIn} onSelect={handlePlanSelect} />
      </div>

      {/* ── SMS pricing ── */}
      <SmsPricingTable />

      {/* ── CTA ── */}
      <CTASection />

      {/* ── How we count ── */}
      {showBillingTransparency && (
        <>
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
              Billing transparency
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
              How we count
            </h2>
          </div>

          <div className="divide-y divide-gray-100">

            <DefinitionBlock
              eyebrow="Customer accounts"
              heading="We count wallets with a balance — nothing else."
              body="A customer account is one shopper wallet that holds a balance greater than zero. The moment a wallet empties out — whether a customer spent it all or it expired — it stops counting on the next billing day. A store with 50,000 registered shoppers but only 800 active balances pays for 800. You never get charged for dormant customers."
              image={CustomerInterface}
              imageAlt="Customer wallet interface"
              flip={false}
            />

            <DefinitionBlock
              eyebrow="Store accounts"
              heading="Each connected Shopify store counts as one."
              body="A store account is a single Shopify storefront connected to Pabloo. If you run a flagship store and a wholesale portal as two separate Shopify stores, that counts as two store accounts. Growth plans support up to 10. If you're running 20 or more, talk to us about the Managed tier — stores are unlimited there."
              image={PablooDashboardImage}
              imageAlt="Pabloo dashboard showing multiple stores"
              flip={true}
            />

            <DefinitionBlock
              eyebrow="Credit events"
              heading="One event = one action. The dollar amount is irrelevant."
              body="A credit event is a single issuance or a single redemption — that's it. Whether you issue $0.50 as a welcome bonus or $5,000 to a high-value customer, each issuance is one event. Whether a customer redeems $2 or drains a $2,000 balance at checkout, each redemption is one event. We built it this way deliberately: volume pricing that scales with transactions, not with the size of your program, keeps your margins predictable no matter how aggressive your cashback rates are."
              image={DashboardImage}
              imageAlt="Credit event activity dashboard"
              flip={false}
            />

            <DefinitionBlock
              eyebrow="Credit issued"
              heading="Unlimited. We mean it."
              body="Credit issued is never metered on any plan. A merchant running a 10% cashback campaign on a $10,000,000 revenue month pays the exact same subscription price as a merchant issuing $50 of welcome credits. This is intentional — metering the dollar amount of credit issued would penalize the most successful programs. Your subscription covers the infrastructure; what you do with it is entirely up to you."
              image={StoreCreditCheckoutMobile}
              imageAlt="Store credit at checkout on mobile"
              flip={true}
            />

            <div className="py-10">
              <p className="text-xs font-bold uppercase tracking-widest text-green-700 mb-2">
                Team accounts
              </p>
              <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-3">
                Add every team member. Always free.
              </h3>
              <p className="text-gray-500 text-base leading-relaxed max-w-2xl">
                A team account is one merchant staff login — a customer service rep, a marketing manager, a developer, a co-founder. Team accounts are unlimited on every plan including Starter. We don't believe in charging more because your business has more than one person running it.
              </p>
            </div>

          </div>
        </>
      )}

    </div>
  );
}
